import SettingNotFoundException from './exception/settingNotFoundException'
import * as promises from './interruptiblePromises'
import { evaluateXpath, getNearestElementMatch } from './interruptibleXpath'

async function getCheckbox(
  settingText: string,
  nodeName = '*',
): Promise<HTMLInputElement> {
  const labelXpath = `descendant::${nodeName}[text() = "${settingText}"]`

  const getCheckboxFromNode = (node: Node) => {
    const label = evaluateXpath<HTMLElement>(node, labelXpath)[0]
    if (label) {
      const checkbox = getNearestElementMatch<HTMLInputElement>(
        label,
        'input',
        'following',
      )
      if (checkbox) {
        return checkbox
      }
    }
    return null
  }

  return promises.getWaitForElementToAppearPromise(
    document,
    getCheckboxFromNode,
    5000,
    true,
  )
}

/**
 * Toggles checkbox based on associated setting label.
 *
 * @param settingText Label text that checkbox is associated with
 */
async function toggleCheckbox(
  settingText: string,
  nodeName = '*',
): Promise<void> {
  const checkbox = await getCheckbox(settingText, nodeName)

  if (!checkbox) {
    throw new Error(`Could not find checkbox for ${settingText}`)
  }

  checkbox.click()
}

/**
 * Returns value of a desired checkbox as a boolean.
 *
 * @param settingText Label text that checkbox is associated with
 */
async function getCurrentValueForCheckbox(
  settingText: string,
  nodeName = '*',
): Promise<boolean> {
  const checkbox = await getCheckbox(settingText, nodeName)
  if (!checkbox) {
    throw new SettingNotFoundException(
      `Could not find checkbox for ${settingText}`,
    )
  }
  return checkbox.checked
}

/**
 * Saves boolean value of a desired checkbox to local storage.
 *
 * @param updateStoredInitialValuesFn Function to update the stored initial value.
 * @param mapToSettingsLabel Map from local storage keys to label for a checkbox.
 * @param settingKey Particular key we are interested in saving a value for.
 * @param recommendationKey Recommendation key for the card we are currently on.
 */
function saveCurrentValueForCheckbox(
  updateStoredAutomationErrorsFn: (Object, string?) => Promise<void>,
  updateStoredInitialValuesFn: (Object, string?) => Promise<void>,
  mapToSettingsLabel: any,
  settingKey: string,
  recommendationKey: any,
  nodeName = '*',
): Promise<void> {
  if (!(settingKey in mapToSettingsLabel)) {
    throw Error(`Could not recognize ${settingKey}`)
  }

  return getCurrentValueForCheckbox(mapToSettingsLabel[settingKey], nodeName)
    .then((value) => {
      updateStoredInitialValuesFn(
        {
          [settingKey]: value,
        },
        recommendationKey,
      )
    })
    .catch((error) => {
      updateStoredAutomationErrorsFn(
        {
          [settingKey]: {
            gatherError: { name: error.name, message: error.message },
          },
        },
        recommendationKey,
      )
    })
}

export {
  getCheckbox,
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
}
